import { defineMessages } from 'react-intl';

export const eventRegistrationFormMessages = defineMessages({
  blockTitle: {
    id: 'eventRegistrationForm_blockTitle',
    defaultMessage: 'Event-Anmeldeformular',
  },
  firstName: {
    id: 'eventRegistrationForm_firstName',
    defaultMessage: 'Vorname',
  },
  lastName: {
    id: 'eventRegistrationForm_lastName',
    defaultMessage: 'Nachname',
  },
  email: {
    id: 'eventRegistrationForm_email',
    defaultMessage: 'E-Mail',
  },
  phone: {
    id: 'eventRegistrationForm_phone',
    defaultMessage: 'Telefonnummer',
  },
  street: {
    id: 'eventRegistrationForm_street',
    defaultMessage: 'Strasse',
  },
  zip: {
    id: 'eventRegistrationForm_zip',
    defaultMessage: 'PLZ',
  },
  city: {
    id: 'eventRegistrationForm_city',
    defaultMessage: 'Ort',
  },
  // Event-registration-only title overrides. The radio choices and the
  // data-privacy static text themselves come from the shared special-field
  // factories (config/specialFormFields) via the translation catalog, so they
  // are intentionally NOT duplicated here.
  contactAllowed: {
    id: 'eventRegistrationForm_contactAllowed',
    defaultMessage:
      'Wir würden auch in Zukunft gerne auf Angebote der FHNW hinweisen und bitten dafür um Einverständnis',
  },
  dataProtectionInfoTitle: {
    id: 'eventRegistrationForm_dataProtectionInfoTitle',
    defaultMessage: 'Datenschutz-Hinweis',
  },
  submitLabel: {
    id: 'eventRegistrationForm_submitLabel',
    defaultMessage: 'Anmeldung abschicken',
  },
  eventName: {
    id: 'eventRegistrationForm_eventName',
    defaultMessage: 'Eventname',
  },
  occasionName: {
    id: 'eventRegistrationForm_occasionName',
    defaultMessage: 'Durchführungsname',
  },
  occasionDate: {
    id: 'eventRegistrationForm_occasionDate',
    defaultMessage: 'Durchführungsdatum',
  },
  occasionPlace: {
    id: 'eventRegistrationForm_occasionPlace',
    defaultMessage: 'Durchführungsort',
  },
});

// Fixed mail-template strings written verbatim into the saved block data.
// They contain volto-form-block placeholders (${eventname}, ${datum},
// ${formfields}) consumed server-side at submit time, so they must NOT pass
// through react-intl (which would try to interpret ${...} as ICU vars). Instead
// of a single German set they are keyed by language and selected from the page
// locale at block-creation time, so a form built on an EN/FR page bakes in the
// matching mail texts (#1662 testing feedback note_800770).
export type MailDefaults = {
  success: string;
  thankyou: string;
  subject: string;
  mailHeader: string;
};

/* eslint-disable no-template-curly-in-string */
export const MAIL_DEFAULTS_BY_LANG: Record<string, MailDefaults> = {
  de: {
    success: 'Vielen Dank für die Anmeldung',
    thankyou: 'Die folgenden Daten haben wir erhalten: <br>${formfields}',
    subject: 'Bestätigung Anmeldung "${eventname}"',
    mailHeader:
      'Vielen Dank für die Anmeldung "${eventname}" vom ${datum}. Folgende Daten haben wir erhalten:',
  },
  en: {
    success: 'Thank you very much for signing up',
    thankyou: 'We have received the following data: <br>${formfields}',
    subject: 'Confirmation of registration for "${eventname}"',
    mailHeader:
      'Thank you very much for registering for "${eventname}" on ${datum}. We have received the following details:',
  },
  fr: {
    success: 'Merci beaucoup pour votre inscription',
    thankyou: 'Nous avons reçu les données suivantes : <br>${formfields}',
    subject: "Confirmation de l'inscription à « ${eventname} »",
    mailHeader:
      'Merci beaucoup pour votre inscription à « ${eventname} » du ${datum}. Nous avons bien reçu les informations suivantes :',
  },
};
/* eslint-enable no-template-curly-in-string */

export const DEFAULT_MAIL_LANG = 'de';

// Resolve the mail texts for a page locale. Region subtags ("en-US") collapse
// to the base language; unknown languages fall back to German.
export const getMailDefaults = (locale?: string): MailDefaults => {
  const lang = (locale ?? '').split('-')[0].toLowerCase();
  return (
    MAIL_DEFAULTS_BY_LANG[lang] ?? MAIL_DEFAULTS_BY_LANG[DEFAULT_MAIL_LANG]
  );
};
